<template>
  <div>
    <component
      :is="child"
      :key="formStore.form.values[props.dependentOn]"
      :options="options"
      :is-required="isRequiredLocal"
      :is-disabled="!isRequiredLocal"
      :field-name="fieldName"
      :label="label"
      :rules="rules"
    ></component>
  </div>
</template>

<script setup>
/**
 * How to use:
 * when building your schema, (in useSchema composable)
 * use this shape to generate this component:
 * {
 *   component: Component,
 *   label: String,
 *   placeholder: String,
 *   isRequired: Boolean,
 *   dependentOn: String,
 *   api: {
 *     path: String,
 *     params: Object,
 *     extraParams: { identifier: String }
 *   }
 * }
 *
 * dependentOn will be the reactive value in which this component
 * will rely on to rebuild. this enables this component to fetch new data
 * using props.api when the watch for props.dependentOn triggers
 *
 * extraParams.identifier will be injected into params.
 * it's just a handy way to insert dynamic params into the api
 */
import axios from '@/services'
import { useFormStore } from '@/stores/form'
import { ref, watch } from 'vue'

const formStore = useFormStore()

const props = defineProps({
  fieldName: {
    type: String,
    required: true
  },
  label: {
    type: String,
    required: true
  },
  placeholder: String,
  isRequired: Boolean,
  isDisabled: [Boolean, Function],
  modelValue: {
    required: true,
    type: [String, Number]
  },
  initialValue: String,
  maxLength: Number,
  rules: Object,
  dependentOn: String,
  api: Object,
  child: {
    type: Function,
    required: true
  }
})

const options = ref()
const isRequiredLocal = ref()

watch(
  () => formStore.form.values[props.dependentOn],
  async (newValue) => {
    await fetchNewOptions(newValue)

    /**
     * special rule for religion-faith pair
     */
    if (props.fieldName == 'faithId' && props.dependentOn == 'religionId') {
      isRequiredLocal.value = [1, 2].includes(+newValue)
    } else {
      isRequiredLocal.value = props.isRequired || false
    }
  }
)

async function fetchNewOptions(newValue) {
  const params = { ...props.api.params }

  let path = props.api.path
  if (props.api.extraParams.replaceInPath) {
    path = path.replaceAll(props.api.extraParams.identifier, newValue)
  } else {
    params[props.api.extraParams.identifier] = newValue
  }

  const data = await axios.get(path, {
    params,
    headers: props.api.headers
  })

  options.value = data.map((d) => {
    return {
      ...d,
      label: d.localizedName,
      value: d.id
    }
  })
}
</script>

<style lang="scss" scoped></style>
